import {
  ANON_ID_URL_PARAM,
  hasStripableQueryParams,
  isStrippedQueryParam,
  SESSION_STORAGE_KEY
} from "./constants";

export interface AbbTrackingContext {
  anon_id: string | null;
}

const storageAvailable = () => globalThis.sessionStorage !== undefined;

function readParam(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key);
  if (raw === null) return null;

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function removeStripableParamsFromUrl(params: URLSearchParams): void {
  const { history, location } = globalThis;

  if (!history?.replaceState || !location) return;

  const keysToDelete: string[] = [];

  for (const key of params.keys()) {
    if (isStrippedQueryParam(key)) {
      keysToDelete.push(key);
    }
  }

  for (const key of keysToDelete) {
    params.delete(key);
  }

  const search = params.toString();
  const query = search ? `?${search}` : "";
  const nextUrl = `${location.pathname}${query}${location.hash}`;

  history.replaceState(history.state, "", nextUrl);
}

export function readAbbTrackingContext(): AbbTrackingContext | null {
  if (!storageAvailable()) return null;

  const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as AbbTrackingContext;
  } catch {
    return null;
  }
}

export function captureAbbTrackingFromUrl(
  search = globalThis.location?.search ?? ""
): void {
  if (!storageAvailable()) return;

  const params = new URLSearchParams(search);
  const anonIdFromUrl = readParam(params, ANON_ID_URL_PARAM);

  if (anonIdFromUrl !== null) {
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ anon_id: anonIdFromUrl })
    );
  } else if (!sessionStorage.getItem(SESSION_STORAGE_KEY)) {
    sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ anon_id: null })
    );
  }

  if (hasStripableQueryParams(params)) {
    removeStripableParamsFromUrl(params);
  }
}
